import { useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { createReorderPoint } from '../../../api/warehouse';

interface WarehouseReorderPointModalProps {
  warehouseId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const WarehouseReorderPointModal = ({
  warehouseId,
  onClose,
  onSuccess,
}: WarehouseReorderPointModalProps) => {
  const [formData, setFormData] = useState({
    inventoryItemId: '',
    defaultReorderThreshold: '0',
    defaultReorderQuantity: '1',
    overrideReorderThreshold: '',
    overrideReorderQuantity: '',
    notes: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.inventoryItemId) {
      setError('Inventory item is required');
      return;
    }

    if (!formData.defaultReorderThreshold) {
      setError('Default reorder threshold is required');
      return;
    }

    if (!formData.defaultReorderQuantity) {
      setError('Default reorder quantity is required');
      return;
    }

    setLoading(true);
    try {
      await createReorderPoint(warehouseId, {
        inventoryItemId: formData.inventoryItemId,
        defaultReorderThreshold: parseInt(formData.defaultReorderThreshold),
        defaultReorderQuantity: parseInt(formData.defaultReorderQuantity),
        overrideReorderThreshold: formData.overrideReorderThreshold
          ? parseInt(formData.overrideReorderThreshold)
          : null,
        overrideReorderQuantity: formData.overrideReorderQuantity
          ? parseInt(formData.overrideReorderQuantity)
          : null,
        notes: formData.notes || undefined,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create reorder point');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-bark-900 dark:text-stone-100">
            Add Reorder Point
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1">
              Inventory Item *
            </label>
            <select
              name="inventoryItemId"
              value={formData.inventoryItemId}
              onChange={handleChange}
              disabled={loading}
              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 disabled:opacity-50"
            >
              <option value="">Select an item type</option>
              <optgroup label="Pots">
                <option value="pots">Pots</option>
              </optgroup>
              <optgroup label="Tree Species">
                <option value="">-- Add tree species IDs as they become available --</option>
              </optgroup>
            </select>
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
              Select pots or a tree species to track inventory thresholds
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1">
                Default Threshold *
              </label>
              <input
                type="number"
                name="defaultReorderThreshold"
                value={formData.defaultReorderThreshold}
                onChange={handleChange}
                min="0"
                disabled={loading}
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1">
                Default Quantity *
              </label>
              <input
                type="number"
                name="defaultReorderQuantity"
                value={formData.defaultReorderQuantity}
                onChange={handleChange}
                min="1"
                disabled={loading}
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 disabled:opacity-50"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1">
              Override Threshold (Optional)
            </label>
            <input
              type="number"
              name="overrideReorderThreshold"
              value={formData.overrideReorderThreshold}
              onChange={handleChange}
              min="0"
              disabled={loading}
              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 disabled:opacity-50"
              placeholder="Leave blank to use default"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1">
              Override Quantity (Optional)
            </label>
            <input
              type="number"
              name="overrideReorderQuantity"
              value={formData.overrideReorderQuantity}
              onChange={handleChange}
              min="1"
              disabled={loading}
              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 disabled:opacity-50"
              placeholder="Leave blank to use default"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              disabled={loading}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 disabled:opacity-50 resize-none"
              placeholder="Optional notes about this reorder point"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-stone-50 dark:hover:bg-[#242d28] transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-moss-600 text-white rounded-lg hover:bg-moss-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WarehouseReorderPointModal;
